// THE PARTY PAGE (UI rebuild batch 2) — work with one party without leaving it. Astra's direction, adapted to the Books:
//
//   head      the party's name (and its review flag), one muted line (kind · standing · the marks that are not the
//             default: an inactive status, a pending or rejected KYC, the running account, a name to review), what they deal
//             in (trade_profile pills; "Last traded" until a profile says something); More (the details sheet), Book trade,
//             and RECORD MONEY — the one gold primary, opening in the direction the box calls for.
//   tabs      Overview · Statement, the tab in the URL (?tab=), so Back and a shared link land on it.
//   Overview  ONLY WHAT HE USES (his words, 1 Nov 2026: "things I see on customer page is somehow irrelevant"): the balances
//             as a figure strip, one cell per asset in the box's words, "Held in Flagged · X USDT" as an information label,
//             Convert balance beside them; Outstanding work only when a settle-later deal is open; Recent activity — the
//             newest five entries of the last 90 days in the Statement's own rows. Party details, the doors (Between
//             parties, Sub-broker trade, Adjust balance, Wallet register) and what rode in on the running account are in
//             the More sheet.
//   Statement laid out as Money movements (PartyStatementTab): search, Filters (Asset, Show reversed), the period (All
//             time), the balances it ties to, one register newest first — Entry / reference · Movement · Balance after ·
//             Type — and the registers' footer.
//   A row     opens where it stands: a trade the deal sheet (?deal=, with Void); a money entry Money movements' own
//             receipt (?entry=, with Reverse).
//
// Behaviour kept from screens/PartyDetail.tsx: the same reads and keys (['party', slug, entity], ['settlements', entity]),
// ONE first paint (the page waits for both, 17 Sept 2026), a failed read keeps the head and draws one line with Try again,
// a desk account's page goes to Treasury, every hook above every return, the Promote confirm and its result line, the
// running-account confirm in both directions and its audit, the Record money direction the box calls for, the doors with
// the party in them (Book trade, Move to a party, Convert balance — under the balances when the box holds something —, Sub-broker
// trade for merchants, Adjust balance — the existing door, never promoted), Settle later's rows from /settle with SettleButton and their leg words.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { errorWords, readWords } from '../../lib/errors';
import { displayAsset, fmtAmount, fmtDay, smartCase } from '../../lib/format';
import {
  partyName, needsNameReview, compareRelevance, sameAsset, partyTypeLabel, trustTierLabel, dustFor, carriedRodeWords,
  RUNNING_ACCOUNT_TITLE, shortReference,
} from '../../lib/naming';
import { isOwnAccount as isOwnParty, treasuryHref } from '../../lib/ownAccounts';
import { tradeProfileWords } from '../../lib/trade-profile';
import { readState, offlineFailure } from '../../lib/read-state';
import { tg } from '../../lib/telegram';
import { boxParts } from '../../components/BoxLines';
import { GroupingPanel } from '../../components/GroupingPanel';
import { ErrorLine } from '../../components/kit';
import { PageHead, Panel, Tabs, Notice, Fact, SummaryStrip, DetailSheet, SkeletonRows, EmptyState, StateLine, ReadFailed, TAP, OfflineNotice } from '../../components/v2/kit';
import type { DealRow, TradeRow } from '../../lib/api';
import { EntryView } from '../../components/v2/movements/EntryView';
import { ReversedLine, type ReversedOutcome } from '../../components/ReverseEntry';
import { PartyStatementTab } from '../../components/v2/parties/PartyStatementTab';
import { entryDoors, particulars, type EntryDoors, type JournalRow } from '../../components/v2/parties/journal';
import { TradeDetail } from '../../components/v2/trades/TradeDetail';
import { PartyWallets } from '../../components/v2/parties/PartyWallets';
import { RecentActivity } from '../../components/v2/parties/RecentActivity';
import { OutstandingRow } from '../../components/v2/parties/OutstandingRow';

const STATUS_TEXT: Record<string, string> = { active: 'Active', inactive: 'Inactive', archived: 'Archived' };
const KYC_TEXT: Record<string, string> = { none: 'None', pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };
const ENTITY_TEXT: Record<string, string> = { dubaiotc: 'DubaiOTC', indiaotc: 'IndiaOTC', euotc: 'EuOTC' };

const REVIEW_NOTE: Record<'profanity' | 'ambiguous' | 'empty', string> = {
  profanity: 'Flagged for review — shown exactly as stored, never auto-renamed.',
  ambiguous: 'Ambiguous — too little to identify. Rename it when you know who.',
  empty: 'No stored name — this is the books’ own label for the record.',
};

/** Notes the migration wrote, not the operator. */
const PROVENANCE = /^(Imported from legacy|Created 20\d\d-)/;

// Astra's two views. Identity and Wallets open from Party details' "More" (a sheet, `?more=1`); an old `?tab=identity` or
// `?tab=wallets` link opens that sheet.
const TABS = ['overview', 'statement'] as const;
type TabId = typeof TABS[number];
const TAB_LABEL: Record<TabId, string> = { overview: 'Overview', statement: 'Statement' };

const BTN = 'inline-flex min-h-11 items-center justify-center px-4 text-body lg:min-h-10';

export function PartyV2() {
  const { slug } = useParams<{ slug: string }>();
  const [search, setSearch] = useSearchParams();
  const entity = search.get('entity') ?? 'dubaiotc';
  const rawTab = search.get('tab');
  const tab: TabId = (TABS as readonly string[]).includes(rawTab ?? '') ? (rawTab as TabId) : 'overview';
  const moreOpen = search.get('more') === '1' || rawTab === 'identity' || rawTab === 'wallets';
  const nav = useNavigate();
  const qc = useQueryClient();

  // EVERY HOOK ABOVE EVERY RETURN (2 Sept 2026: a hook below an early return blacked out the console).
  const q = useQuery({ queryKey: ['party', slug, entity], queryFn: () => api.partyDetail(slug!, entity), enabled: !!slug });
  const promote = useMutation({
    mutationFn: () => api.promoteParty(slug!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['party', slug] });
      qc.invalidateQueries({ queryKey: ['parties'] });
      tg()?.HapticFeedback.notificationOccurred('success');
    },
  });
  // Settle later from /settle — the same read Home and Settle later use, so the three agree by construction.
  const settle = useQuery({ queryKey: ['settlements', entity], queryFn: () => api.settlements(entity), refetchOnWindowFocus: true });
  const [reversed, setReversed] = useState<ReversedOutcome | null>(null);
  // A row that folds several entries (a trade and the money linked to its deal): its doors, one each, in a sheet.
  const [doorsOf, setDoorsOf] = useState<EntryDoors | null>(null);
  // The party's deals as the deal sheet reads them (TradeRow), for a row that opens its deal and for the receipt's deal link.
  const dealsById = useMemo(() => new Map<string, TradeRow>((q.data?.deals ?? []).map((d) => [d.deal_id, asTradeRow(d, q.data!.party)])), [q.data]);

  /** A tab's link: this page's own query string (entity …) with the tab set — Overview is the bare page. */
  const tabTo = (id: TabId) => {
    const p = new URLSearchParams(search);
    if (id === 'overview') p.delete('tab'); else p.set('tab', id);
    const s = p.toString();
    return s ? `?${s}` : '?';
  };
  const moreTo = (() => { const p = new URLSearchParams(search); p.delete('tab'); p.set('more', '1'); return `?${p}`; })();
  const closeMore = () => setSearch((prev) => {
    const p = new URLSearchParams(prev); p.delete('more');
    if (p.get('tab') === 'identity' || p.get('tab') === 'wallets') p.delete('tab');
    return p;
  }, { replace: true });
  const back = <button type="button" onClick={() => nav(-1)} className={`${TAP} mb-2 inline-block`}>← Back</button>;

  // A FAILED READ KEEPS THE HEAD: the name from the slug, one line, Try again — never an empty party.
  if (q.isError) return (
    <>
      {back}
      <PageHead title={partyName({ slug })} />
      {offlineFailure(q.error) ? <OfflineNotice what="the party" /> : <Notice tone="bad">
        <StateLine tone="bad" more={readWords(q.error)} action={<button type="button" className={TAP} disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>}>
          Couldn&rsquo;t read the party.
        </StateLine>
      </Notice>}
    </>
  );
  // A desk account has no page here: Treasury opens it (operator, 21 Oct 2026).
  if (q.data && isOwnParty(q.data.party)) return <Navigate to={treasuryHref(q.data.party.treasury_slug)} replace />;
  // ONE FIRST PAINT: both reads (the settle read counts once it has data or has failed).
  if (!q.data || settle.isLoading) return (
    <>
      {back}
      <PageHead title={partyName({ slug })} />
      <Panel flush><SkeletonRows rows={4} what="the party" /></Panel>
    </>
  );

  const { party, balances, wallets, children } = q.data;
  const carried = q.data.carried_on_account ?? null;
  const ridden = carried ? carriedRodeWords(carried.rode) : '';
  const ridesOnAccount = party.trade_remainder_rides_on_account === true;
  const isMerchant = party.party_type === 'counterparty';
  const partyEntity = party.entity ?? entity;

  const title = partyName(party);
  const review = needsNameReview(party.display_name ?? '');
  const storedDiffers = (party.display_name ?? '').trim() !== title;
  const otherDesk = party.entity && party.entity !== 'dubaiotc' ? `${ENTITY_TEXT[party.entity] ?? smartCase(party.entity)} · ` : '';
  // ONE QUIET LINE under the name: the role, the running account when it is on (a term — it changes when trades close),
  // "Needs review" when the stored name does (one word, its reason in the sheet).
  // WHAT THEY DEAL IN (the operator, 27 Oct: "Buyer and seller /MC /bank transfer etc also should be clearly visible on
  // customer card"): the party read's trade_profile (db/0101, main dcb3085) — the side and the methods as pills under the
  // name, a label only (no rail follows from it). Until a profile says something (a Worker before dcb3085, 0101 not
  // applied, nothing known yet) the stand-in stays: "Last traded", the side of the newest trade that was not voided —
  // history, not a profile, and said as such.
  const profile = tradeProfileWords(party.trade_profile);
  const lastDeal = profile ? undefined : [...(q.data.deals ?? [])].filter((d) => d.lifecycle !== 'voided')
    .sort((x, y) => Date.parse(y.booked_at) - Date.parse(x.booked_at))[0];
  const lastTraded = lastDeal
    ? `Last traded: ${lastDeal.direction === 'customer_buy' ? `Buys ${displayAsset(lastDeal.asset_give)}` : `Sells ${displayAsset(lastDeal.asset_take)}`}`
    : '';
  // The marks that are NOT the default (Party details left the Overview, 1 Nov 2026 — these are what it said that matters).
  const statusMark = party.status && party.status !== 'active' ? STATUS_TEXT[party.status] ?? smartCase(party.status) : '';
  const kycMark = party.kyc_status === 'pending' || party.kyc_status === 'rejected' ? `KYC ${party.kyc_status}` : '';
  const subtitle = [otherDesk + partyTypeLabel(party.party_type), statusMark, kycMark, lastTraded, ridesOnAccount ? RUNNING_ACCOUNT_TITLE : '', review ? 'Needs review' : ''].filter(Boolean).join(' · ');

  // WHICH WAY THE BOX POINTS — per asset, never one number: Record money opens in the direction the box calls for.
  const boxes = Object.entries(balances ?? {}).filter(([a, v]) => Math.abs(v ?? 0) > dustFor(a));
  const theyOwe = boxes.some(([, v]) => v > 0);
  const deskOwes = boxes.some(([, v]) => v < 0);
  const pq = `party=${encodeURIComponent(party.slug)}`;
  const ent = entity === 'dubaiotc' ? '' : `entity=${entity}`;
  const recordHref = `/record?${pq}&dir=${deskOwes && !theyOwe ? 'out' : 'in'}`;
  // CONVERT BALANCE BY THE BALANCES (his "convert button seems hidden inside the party", 31 Oct 2026): a button under the figures
  // it converts, whenever the box holds something; it left the "More doors" row under Recent activity, where he did not find it.
  const convertHref = `/parties/${encodeURIComponent(party.slug)}/convert${ent ? `?${ent}` : ''}`;
  // The rarer doors, in the More sheet (his "irrelevant", 1 Nov 2026: a row of chips under every party).
  const doors: Array<{ to: string; label: string }> = [
    ...(boxes.length === 0 ? [{ to: convertHref, label: 'Convert balance' }] : []),
    ...(party.entity !== 'indiaotc' ? [{ to: `/record?mode=p2p&from=${encodeURIComponent(party.slug)}${ent ? `&${ent}` : ''}`, label: 'Between parties' }] : []),
    ...(isMerchant ? [{ to: `/parties/${encodeURIComponent(party.slug)}/sub-broker${ent ? `?${ent}` : ''}`, label: 'Sub-broker trade' }] : []),
    // Adjust balance: the existing door, at the end, a plain secondary — never promoted (ruling C1).
    { to: `/treasury/adjust?${pq}`, label: 'Adjust balance' },
  ];

  /* ─── Balances: a figure strip, one cell per asset, in the box's words ─── */
  const assetRows = Object.entries(balances).map(([asset, balance]) => ({ asset, balance })).sort(compareRelevance);
  const flagged = Object.entries(q.data.flagged_held ?? {}).filter(([, v]) => Number.isFinite(v) && v !== 0);
  // HELD IN FLAGGED as a label and its figure, not a pill holding a figure (his "one fact per place", 1 Nov 2026).
  const flaggedPill = (a: string, v: number) => (
    <span key={a} className="inline-flex items-baseline gap-2 whitespace-nowrap text-meta">
      <span className="text-tg-link">Held in Flagged</span>
      <span><span className="font-mono tabular text-body text-tg-text">{fmtAmount(v, a)}</span> <span className="text-tg-hint">{displayAsset(a)}</span></span>
    </span>
  );
  // HELD IN FLAGGED sits beside its own asset's cell (information, never part of the figure); an asset with no cell of its
  // own gets its label under the strip.
  const flaggedFor = (asset: string) => flagged.find(([a]) => sameAsset(a, asset));
  const flaggedLoose = flagged.filter(([a]) => !assetRows.some((r) => sameAsset(r.asset, a)));
  const stripItems = assetRows.length === 0
    // The party read succeeded and its box is empty: square (the page draws nothing before a successful read).
    ? [{ label: 'All assets', value: <span className="text-tg-hint">square</span> }]
    : assetRows.map(({ asset, balance }) => {
      const { words, figure } = boxParts(balance, asset);
      const f = flaggedFor(asset);
      return {
        label: `${displayAsset(asset)} · ${words}`,
        value: figure ? <span className={balance < 0 ? 'text-neg' : ''}>{figure}</span> : <span className="text-tg-hint">square</span>,
        sub: f ? flaggedPill(f[0], f[1]) : undefined,
      };
    });
  const balancesStrip = (
    <>
      <SummaryStrip items={stripItems} />
      {(boxes.length > 0 || flaggedLoose.length > 0) && (
        <div className="-mt-2 mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 lg:-mt-3 lg:mb-6">
          {boxes.length > 0 && <Link to={convertHref} className={`btn-secondary ${BTN}`}>Convert balance</Link>}
          {flaggedLoose.map(([a, v]) => flaggedPill(a, v))}
        </div>
      )}
    </>
  );

  /* ─── Outstanding work: the Settle later deals still open on this party ─── */
  const awaitingDeals = settle.data?.parties.find((p) => p.slug === party.slug)?.deals ?? [];
  const settleState = readState(settle);
  // ONLY WHEN SOMETHING IS OPEN (or its read failed): nothing open draws nothing.
  const nothingOpen = settleState === 'known' && awaitingDeals.length === 0;
  const outstandingPanel = nothingOpen ? null : (
    <Panel title="Outstanding work" action={awaitingDeals.length > 0 ? <Link to={`/settle?q=${encodeURIComponent(title)}`} className={TAP}>Settle later →</Link> : undefined}>
      {settleState === 'failed' ? (
        <ReadFailed what="settle-later trades" error={settle.error} onRetry={() => settle.refetch()} tone="bad" />
      ) : settleState === 'known' && awaitingDeals.length === 0 ? (
        // AN EMPTY CLAIM NEEDS A SUCCESSFUL READ — the settle read answered, with nothing open for this party.
        <EmptyState className="!py-4">Nothing open.</EmptyState>
      ) : (
        <div>
          {awaitingDeals.map((d) => <OutstandingRow key={d.deal_id} deal={d} partySlug={party.slug} />)}
        </div>
      )}
    </Panel>
  );

  /* THE DEAL OPENS HERE (a review, 1 Nov 2026): the Trades log holds the desk's latest 200, so a quieter party's deal could
     open there to nothing. The party's own read carries its deals: the deal sheet opens on this page, Back closes it. */
  const dealOpen = search.get('deal');
  const openDealRow = dealOpen ? dealsById.get(dealOpen) ?? null : null;
  const dealLifecycle = (id: string) => dealsById.get(id)?.lifecycle;
  const closeDeal = () => { const n = new URLSearchParams(search); n.delete('deal'); setSearch(n, { replace: true }); };
  const openDeal = (id: string) => { const n = new URLSearchParams(search); n.delete('entry'); n.set('deal', id); setSearch(n); };

  /* A STATEMENT ROW OPENS WHERE IT STANDS (journal.ts entryDoors): a trade alone opens the deal sheet (its Void) when the
     party's read holds the deal, else the trade's own receipt; ONE money entry opens Money movements' receipt (its own
     Reverse door) — a transfer linked to a deal is still a money entry; a row folding several entries lists each door. */
  const entryOpen = search.get('entry');
  const openEntry = (txn: string) => { setDoorsOf(null); const n = new URLSearchParams(search); n.delete('deal'); n.set('entry', txn); setSearch(n); };
  const openRow = (r: JournalRow) => {
    const d = entryDoors(r);
    const dealHere = !!d.deal && dealsById.has(d.deal);
    if (d.money.length === 0 && d.tradeTxn) { if (dealHere && d.deal) openDeal(d.deal); else openEntry(d.tradeTxn); return; }
    if (d.money.length === 1 && !d.tradeTxn) { openEntry(d.money[0]!.txn_id); return; }
    if (d.money.length > 0 || dealHere) setDoorsOf(d);
  };
  const doorsSheet = doorsOf && (
    <DetailSheet title="Entries" onClose={() => setDoorsOf(null)}>
      <ul>
        {doorsOf.deal && dealsById.has(doorsOf.deal) && (
          <li className="border-b border-stroke-soft">
            <button type="button" className="flex min-h-11 w-full items-center justify-between gap-3 text-left text-body text-tg-accent lg:min-h-10"
              onClick={() => { const id = doorsOf.deal!; setDoorsOf(null); openDeal(id); }}>
              <span>Deal <span className="font-mono">{shortReference(doorsOf.deal)}</span></span><span aria-hidden>→</span>
            </button>
          </li>
        )}
        {!(doorsOf.deal && dealsById.has(doorsOf.deal)) && doorsOf.tradeTxn && (
          <li className="border-b border-stroke-soft">
            <button type="button" className="flex min-h-11 w-full items-center justify-between gap-3 text-left text-body text-tg-accent lg:min-h-10"
              onClick={() => openEntry(doorsOf.tradeTxn!)}>
              <span>The trade</span><span aria-hidden>→</span>
            </button>
          </li>
        )}
        {doorsOf.money.map((m) => (
          <li key={m.txn_id} className="border-b border-stroke-soft last:border-b-0">
            <button type="button" className="flex min-h-11 w-full items-center justify-between gap-3 py-2 text-left text-body lg:min-h-10"
              onClick={() => openEntry(m.txn_id)}>
              <span className="min-w-0">
                <span className="block break-words">{particulars(m.words).head}</span>
                {m.reference && <span className="block text-meta text-tg-hint">{shortReference(m.reference)}</span>}
              </span>
              <span aria-hidden className="text-tg-accent">→</span>
            </button>
          </li>
        ))}
      </ul>
    </DetailSheet>
  );
  const closeEntry = () => { const n = new URLSearchParams(search); n.delete('entry'); setSearch(n, { replace: true }); };

  /* ─── More: identity, the running-account mark, notes, wallets — one tap from Party details ─── */
  const noteLines = (party.notes ?? '').split('\n').filter((l) => l.trim() && !PROVENANCE.test(l.trim()));
  const sheetHead = 'mb-2 text-label font-medium uppercase tracking-wider text-tg-hint';
  const moreSheet = moreOpen && (
    <DetailSheet title="Party details" onClose={closeMore}>
      <section>
        {(storedDiffers || review) && (
          <Fact label="Name">
            <span>{title}</span>
            {storedDiffers && <span className="mt-1 block text-meta text-tg-hint">Stored as <span className="font-mono">{party.display_name}</span></span>}
            {review && <span className="mt-1 block text-meta text-tg-hint">{REVIEW_NOTE[review]}</span>}
          </Fact>
        )}
        <Fact label="Role">
          <span>{partyTypeLabel(party.party_type)}</span>
          {party.party_type === 'customer' && (
            <button
              type="button"
              disabled={promote.isPending}
              className={`${TAP} ml-3 disabled:opacity-60`}
              onClick={() => {
                if (confirm(`Promote ${title} to merchant?\n\nAny open balance moves to their merchant account first, then the type changes. This is recorded in the audit log.`)) {
                  promote.mutate();
                }
              }}
            >
              {promote.isPending ? 'Promoting…' : 'Promote to merchant'}
            </button>
          )}
          {promote.isError && <ErrorLine>{errorWords(promote.error)}</ErrorLine>}
          {promote.isSuccess && (
            <span className="mt-1 block text-meta text-pos">
              Now a merchant{promote.data?.moved?.length ? ` — moved ${promote.data.moved.join(', ')}` : ''}.
            </span>
          )}
        </Fact>
        <Fact label="Status">{STATUS_TEXT[party.status] ?? smartCase(party.status)}</Fact>
        <Fact label="Tier">{trustTierLabel(party.trust_tier)}</Fact>
        <Fact label="KYC">{KYC_TEXT[party.kyc_status] ?? smartCase(party.kyc_status)}</Fact>
        {party.telegram_username && <Fact label="Telegram"><code className="font-mono">@{party.telegram_username}</code></Fact>}
        {Number(party.credit_limit_usd ?? 0) > 0 && <Fact label="Credit limit"><span className="amt">{fmtAmount(party.credit_limit_usd, 'AED')} USD-eq</span></Fact>}
        <Fact label="Created">{otherDesk}{fmtDay(party.created_at)}</Fact>
      </section>
      <GroupingPanel
        partyType={party.party_type}
        slug={party.slug}
        isGroup={party.party_type === 'own_account' && !party.treasury_slug}
        parentSlug={party.parent_slug ?? null}
        parentDisplayName={party.parent_display_name ?? null}
        children={children}
      />
      <section className="mt-6">
        <h3 className={sheetHead}>{RUNNING_ACCOUNT_TITLE}</h3>
        <RunningAccountMark slug={party.slug} entity={partyEntity} name={title} on={ridesOnAccount} />
      </section>
      {noteLines.length > 0 && (
        <section className="mt-6">
          <h3 className={sheetHead}>Notes</h3>
          <p className="whitespace-pre-wrap text-body">{noteLines.join('\n')}</p>
        </section>
      )}
      {ridden && (
        <section className="mt-6">
          <h3 className={sheetHead}>Rode in on the account</h3>
          <p className="text-body">{ridden}</p>
        </section>
      )}
      <div className="mt-6">
        <PartyWallets partySlug={party.slug} wallets={wallets} keys={q.data.wallet_keys ?? []} />
      </div>
      <section className="mt-6">
        <h3 className={sheetHead}>Other entries</h3>
        <ul>
          {doors.map((d) => (
            <li key={d.label} className="border-b border-stroke-soft last:border-b-0">
              <Link to={d.to} className="flex min-h-11 items-center text-body text-tg-accent hover:underline lg:min-h-10">{d.label} →</Link>
            </li>
          ))}
        </ul>
      </section>
    </DetailSheet>
  );

  const statementTo = tabTo('statement');

  return (
    <>
      {back}
      <PageHead
        title={<span className="break-words" title={party.display_name ?? title}>{title}</span>}
        sub={subtitle}
        marks={profile && (
          <ul aria-label="What they deal in" className="flex flex-wrap gap-2">
            {profile.side && <li className="pill text-tg-accent bg-tg-accent/10">{profile.side}</li>}
            {profile.methods.map((m) => <li key={m} className="pill text-tg-text bg-panel-3">{m}</li>)}
          </ul>
        )}
        actions={(
          <>
            <Link to={moreTo} className={`btn-secondary ${BTN}`}>More</Link>
            <Link to={`/trade?${pq}`} className={`btn-secondary ${BTN}`}>Book trade</Link>
            <Link to={recordHref} className={`btn-primary ${BTN}`}>Record money</Link>
          </>
        )}
      />

      <Tabs label="Party views" current={tab} items={TABS.map((id) => ({ id, label: TAB_LABEL[id], to: tabTo(id) }))} />

      <ReversedLine outcome={reversed} onDismiss={() => setReversed(null)} className="mb-4" />

      {tab === 'overview' && (
        <>
          {balancesStrip}
          <div className="space-y-4 lg:space-y-6">
            {outstandingPanel}
            <RecentActivity slug={party.slug} entity={partyEntity} balances={balances} statementHref={statementTo} onOpen={openRow} self={party.display_name ?? title} dealLifecycle={dealLifecycle} />
          </div>
        </>
      )}

      {tab === 'statement' && (
        <PartyStatementTab
          slug={party.slug}
          entity={partyEntity}
          balances={balances}
          assets={assetRows.map((r) => r.asset)}
          onOpen={openRow}
          self={party.display_name ?? title}
          dealLifecycle={dealLifecycle}
        />
      )}

      {moreSheet}
      {doorsSheet}
      {entryOpen && !openDealRow && (
        <EntryView
          txn={entryOpen}
          loaded={null}
          entity={partyEntity}
          dealsById={dealsById}
          onOpenDeal={openDeal}
          onReversed={(o) => { setReversed(o); closeEntry(); }}
          onClose={closeEntry}
        />
      )}
      {openDealRow && <TradeDetail deal={openDealRow} onClose={closeDeal} />}
    </>
  );
}

/** THE RUNNING-ACCOUNT MARK (db/0036, decision 1's door): one line and one button, a confirm that says what changes in
 *  both directions, recorded in the audit log by the route; the settle views are pulled in with the party. */
function RunningAccountMark({ slug, entity, name, on }: { slug: string; entity: string; name: string; on: boolean }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (next: boolean) => api.patchParty(slug, { entity: entity as 'dubaiotc' | 'indiaotc', trade_remainder_rides_on_account: next }),
    onSuccess: () => {
      tg()?.HapticFeedback.notificationOccurred('success');
      qc.invalidateQueries({ queryKey: ['party', slug] });
      qc.invalidateQueries({ queryKey: ['parties'] });
      qc.invalidateQueries({ queryKey: ['settlements'] });
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-body">{on ? 'On — remainders ride on this balance.' : 'Off — every leg must be paid in full.'}</p>
        <button
          type="button"
          disabled={m.isPending}
          onClick={() => {
            const ask = on
              ? `Stop carrying remainders for ${name}?\n\nTheir trades will then stay open until every leg is paid in full. Nothing already carried moves — it stays on their balance. This is recorded in the audit log.`
              : `Carry remainders on a running account for ${name}?\n\nA trade leg of theirs will close as soon as money has moved on it, and the remainder — usually their own margin — will ride on their balance, recorded on the deal and in the audit log. A leg nothing has moved on still blocks.`;
            if (confirm(ask)) m.mutate(!on);
          }}
          className={`btn-secondary ${BTN} disabled:opacity-60`}
        >
          {m.isPending ? 'Saving…' : on ? 'Stop carrying remainders' : 'Carry remainders'}
        </button>
      </div>
      <ErrorLine>{m.isError ? errorWords(m.error) : null}</ErrorLine>
    </>
  );
}

/** A party's deal as the deal sheet reads it. */
function asTradeRow(d: DealRow, party: { slug: string; display_name?: string | null }): TradeRow {
  return {
    deal_id: d.deal_id, direction: d.direction, asset_take: d.asset_take, asset_give: d.asset_give, amount_take: d.amount_take,
    amount_give: d.amount_give, rate: d.rate, spread_amount: d.spread_amount, payment_method: d.payment_method, delivery_method: null,
    booked_at: d.booked_at, booked_by_admin: d.booked_by_admin, party_slug: party.slug, party_name: party.display_name ?? partyName(party),
    lifecycle: d.lifecycle, notes: d.notes ?? null,
  };
}
